/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import React from 'react';

import CartFreeShipping from 'Component/CartFreeShipping';
import Link from 'Component/Link';
import { CART_URL } from 'Route/CartPage/CartPage.config';
import {
    CartOverlay as SourceCartOverlay
} from 'SourceComponent/CartOverlay/CartOverlay.component';
import { scrollToTop } from 'Util/Browser';
import { DISPLAY_CART_TAX_IN_SUBTOTAL, DISPLAY_CART_TAX_IN_TOTAL } from 'Util/Cart/Cart';

import './CartOverlay.override.style';

/** @namespace PeggysagePwa/Component/CartOverlay/Component */
export class CartOverlayComponent extends SourceCartOverlay {
    static propTypes = {
        ...SourceCartOverlay.propTypes,
        cartSubtotal: PropTypes.number
    };

    static defaultProps = {
        cartSubtotal: 0
    };

    renderCartItemsCount() {
        const {
            totals: {
                items = []
            }
        } = this.props;

        if (items.length < 1) {
            return null;
        }

        const itemsCount = items.length > 1 ? __('%s items', items.length) : __('1 item');

        return (
            <div block="CartOverlay" elem="ItemsCount">
                { itemsCount }
            </div>
        );
    }

    renderSubtotal() {
        const {
            cartSubtotal,
            cartDisplaySettings: {
                display_tax_in_subtotal
            } = {}
        } = this.props;

        if (!cartSubtotal) {
            return null;
        }

        // eslint-disable-next-line fp/no-let
        let title = '';

        switch (display_tax_in_subtotal) {
        case DISPLAY_CART_TAX_IN_SUBTOTAL.INCL_TAX:
            title = __('Subtotal (Incl. Tax)');
            break;
        case DISPLAY_CART_TAX_IN_SUBTOTAL.EXCL_TAX:
            title = __('Subtotal (Excl. Tax)');
            break;
        default:
            title = __('Subtotal');
        }

        return (
            <dl
              block="CartOverlay"
              elem="Subtotal"
            >
                <dt>{ title }</dt>
                <dd>{ this.renderPriceLine(cartSubtotal) }</dd>
            </dl>
        );
    }

    renderShipping() {
        const { cartShippingPrice } = this.props;

        if (!cartShippingPrice) {
            return null;
        }

        return (
            <dl
              block="CartOverlay"
              elem="Shipping"
            >
                <dt>{ __('Estimated Shipping') }</dt>
                <dd>{ this.renderPriceLine(cartShippingPrice) }</dd>
            </dl>
        );
    }

    renderFreeShipping() {
        return (
            <div block="CartOverlay" elem="FreeShipping">
                <CartFreeShipping />
            </div>
        );
    }

    /**
     * Override: change label
     * @returns {JSX.Element}
     */
    renderTotals() {
        const {
            totals: {
                prices: {
                    grand_total: {
                        value: grand_total = 0
                    } = {}
                } = {}
            },
            cartDisplaySettings: {
                display_tax_in_total
            } = {}
        } = this.props;

        // eslint-disable-next-line fp/no-let
        let title = '';

        switch (display_tax_in_total) {
        case DISPLAY_CART_TAX_IN_TOTAL.INCL_TAX:
            title = __('Order total (Incl. Tax)');
            break;
        case DISPLAY_CART_TAX_IN_TOTAL.EXCL_TAX:
            title = __('Order total (Excl. Tax)');
            break;
        default:
            title = __('Order total');
        }

        return (
            <dl
              block="CartOverlay"
              elem="Total"
            >
                <dt>{ title }</dt>
                <dd>
                    { this.renderPriceLine(grand_total) }
                    { this.renderOrderTotalExlTax() }
                </dd>
            </dl>
        );
    }

    /**
     * Override: change label
     * @returns {JSX.Element|null}
     */
    renderTax() {
        const {
            totals: {
                prices: {
                    applied_taxes = []
                } = {}
            },
            cartDisplaySettings: {
                display_zero_tax_subtotal
            } = {}
        } = this.props;

        if (!applied_taxes[0]?.amount?.value && !display_zero_tax_subtotal) {
            return null;
        }

        return (
            <dl
              block="CartOverlay"
              elem="Tax"
            >
                <dt>{ __('Tax') }</dt>
                <dd>{ this.renderPriceLine(applied_taxes[0]?.amount?.value) }</dd>
            </dl>
        );
    }

    /**
     * Override: multiple coupon codes
     * @returns {JSX.Element|null}
     */
    renderDiscount() {
        const {
            totals: {
                prices: {
                    applied_rule_ids,
                    coupon_code,
                    discount,
                    discounts = []
                } = {}
            }
        } = this.props;

        const { amount: { value: discount_amount = 0 } = {} } = discount || {};

        if (!applied_rule_ids || !discount_amount) {
            return null;
        }

        if (discounts && discounts.length) {
            return discounts.map((discount, index) => {
                const { amount: { value }, label } = discount;
                return (
                    <dl
                      block="CartOverlay"
                      elem="Discount"
                      // eslint-disable-next-line react/no-array-index-key
                      key={ index }
                    >
                        <dt>
                            { label }
                        </dt>
                        <dd>{ `-${this.renderPriceLine(Math.abs(value))}` }</dd>
                    </dl>
                );
            });
        }

        const label = coupon_code ? __('Coupon code discount ') : __('Discount: ');

        return (
            <dl
              block="CartOverlay"
              elem="Discount"
            >
                <dt>
                    { label }
                    { this.renderCouponCode(coupon_code) }
                </dt>
                <dd>{ `-${this.renderPriceLine(Math.abs(discount_amount))}` }</dd>
            </dl>
        );
    }

    /**
     * Override: add subtotal + shipping + free shipping
     * @returns {JSX.Element|null}
     */
    renderCartAdditional() {
        const { totals: { items = [] } } = this.props;

        if (items.length < 1) {
            return null;
        }

        return (
            <div block="CartOverlay" elem="Additional">
                { this.renderSubtotal() }
                { this.renderTax() }
                { this.renderDiscount() }
                { this.renderShipping() }
                { this.renderTotals() }
                { this.renderOutOfStockProductsWarning() }
                { this.renderFreeShipping() }
                { this.renderActions() }
            </div>
        );
    }

    /**
     * Override: remove checkout button
     * @returns {JSX.Element}
     */
    renderActions() {
        return (
            <div block="CartOverlay" elem="Actions">
                <Link
                  block="CartOverlay"
                  elem="CartButton"
                  mix={ { block: 'Button' } }
                  to={ CART_URL }
                  onClick={ scrollToTop }
                >
                    { __('View cart') }
                </Link>
                { /* this.renderSecureCheckoutButton() */ }
            </div>
        );
    }

    /**
     * Override: remove promo + remove Overlay (to open it in popup instead)
     * @returns {JSX.Element}
     */
    render() {
        return (
            <div block="CartOverlay">
                <div block="CartOverlay" elem="ContentWrapper">
                    { this.renderCartItemsCount() }
                    { this.renderCartItems() }
                    { this.renderCartAdditional() }
                </div>
            </div>
        );
    }
}

export default CartOverlayComponent;
